import React from "react";
import { flexRender, Table } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { EmptyIcon } from "@/assets/icons";

interface NoTableDataProps {
  message?: string;
  className?: string;
}

function NoTableData({
  message = "No data available",
  className = "p-8 text-center text-gray-500",
}: NoTableDataProps) {
  return (
    <div className={className}>
      <div className="text-gray-400 mb-2">
        <EmptyIcon className="mx-auto" size={48} />
      </div>
      <p className="text-sm">{message}</p>
    </div>
  );
}

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
}

interface PageInfoProps {
  pagination: PaginationInfo;
}

interface PageNavigationProps {
  pagination: PaginationInfo;
  onPageChange: (page: number) => void;
}

const PAGINATION_BUTTON_CLASSES =
  "flex items-center justify-center w-10 h-10 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 focus:z-10 transition-colors";

function PageInfo({ pagination }: PageInfoProps) {
  const { t } = useTranslation();

  return (
    <div>
      <p className="text-sm text-gray-700">
        {t("pagination.showing")}{" "}
        <span className="font-medium">
          {(pagination.currentPage - 1) * pagination.pageSize + 1}
        </span>{" "}
        -{" "}
        <span className="font-medium">
          {Math.min(
            pagination.currentPage * pagination.pageSize,
            pagination.totalItems
          )}
        </span>{" "}
        {t("pagination.of")}{" "}
        <span className="font-medium">
          {pagination.totalItems.toLocaleString()}
        </span>
      </p>
    </div>
  );
}

function PageNavigation({ pagination, onPageChange }: PageNavigationProps) {
  const { currentPage, totalPages } = pagination;

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      let start = Math.max(1, currentPage - 2);
      const end = Math.min(totalPages, start + maxVisible - 1);

      if (end === totalPages) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) pages.push(i);
    }
    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <nav
      className="inline-flex -space-x-px rounded-md shadow-sm"
      aria-label="Pagination"
    >
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={`${PAGINATION_BUTTON_CLASSES} rounded-l-md bg-white ${
          currentPage === 1
            ? "disabled:opacity-50 disabled:cursor-not-allowed"
            : "cursor-pointer hover:bg-gray-50"
        }`}
        title="Previous page"
      >
        ‹
      </button>

      {pageNumbers.map(page => (
        <button
          key={page}
          onClick={() => onPageChange(page)}
          className={`${PAGINATION_BUTTON_CLASSES} ${
            page === currentPage
              ? "bg-gray-200 text-black border-gray-300 z-10 font-bold cursor-default"
              : "bg-white cursor-pointer hover:bg-gray-50"
          }`}
        >
          {page}
        </button>
      ))}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={`${PAGINATION_BUTTON_CLASSES} rounded-r-md bg-white ${
          currentPage === totalPages
            ? "disabled:opacity-50 disabled:cursor-not-allowed"
            : "cursor-pointer hover:bg-gray-50"
        }`}
        title="Next page"
      >
        ›
      </button>
    </nav>
  );
}

interface HeadlessTableProps<TData> {
  table: Table<TData>;
  title?: string;
  className?: string;
  tableClassName?: string;
  headerClassName?: string;
  bodyClassName?: string;
  rowClassName?: string | ((row: TData, index: number) => string);
  cellClassName?: string;
  onRowClick?: (row: TData) => void;
  pagination?: PaginationInfo;
  onPageChange?: (page: number) => void;
  emptyMessage?: string;
  emptyStateClassName?: string;
  renderRowOverlay?: (row: TData) => React.ReactNode;
}

export default function HeadlessTable<TData>({
  table,
  title,
  className = "",
  tableClassName = "",
  headerClassName = "",
  bodyClassName = "",
  rowClassName = "",
  cellClassName = "",
  onRowClick,
  pagination,
  onPageChange,
  emptyMessage,
  emptyStateClassName,
  renderRowOverlay,
}: HeadlessTableProps<TData>) {
  const { t } = useTranslation();
  if (table.getRowModel().rows.length === 0) {
    return (
      <div className={className}>
        {title && (
          <h2 className="text-lg font-semibold text-black mb-4">{t(title)}</h2>
        )}
        <NoTableData
          message={emptyMessage || t("common.noData")}
          className={emptyStateClassName}
        />
      </div>
    );
  }

  return (
    <div className={`border-gray-300 overflow-hidden bg-white ${className}`}>
      {title && (
        <h2 className="text-lg font-semibold text-black mb-4">{title}</h2>
      )}
      <div className="overflow-x-auto border border-gray-300 rounded-md">
        <table className={`min-w-full table-fixed ${tableClassName}`}>
          <thead
            className={`bg-gray-100 border-gray-100 border-l-3 py-2 ${headerClassName}`}
          >
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  return (
                    <th
                      key={header.id}
                      style={{
                        width: `${header.getSize()}px`,
                        minWidth: `${header.getSize()}px`,
                        maxWidth: `${header.getSize()}px`,
                      }}
                      className="px-1 py-1 text-sm h-[40px] text-center text-wrap font-bold text-black"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className={`bg-white ${bodyClassName}`}>
            {table.getRowModel().rows.map((row, index) => {
              const computedRowClassName =
                typeof rowClassName === "function"
                  ? rowClassName(row.original, index)
                  : rowClassName;

              const overlay = renderRowOverlay?.(row.original);

              return (
                <tr
                  key={row.id}
                  className={`border-b border-gray-200 hover:bg-gray-50 border-l-3 border-l-transparent hover:border-l-3 hover:border-l-primary transition-colors relative ${computedRowClassName}`}
                  onClick={() => onRowClick?.(row.original)}
                  style={{ cursor: onRowClick ? "pointer" : "default" }}
                >
                  {row.getVisibleCells().map((cell, cellIndex) => (
                    <td
                      key={cell.id}
                      style={{
                        width: `${cell.column.getSize()}px`,
                        minWidth: `${cell.column.getSize()}px`,
                        maxWidth: `${cell.column.getSize()}px`,
                      }}
                      className={`px-2 py-3 text-sm text-center text-black align-middle ${cellClassName}`}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                      {cellIndex === 0 && overlay && (
                        <div className="absolute inset-0 z-10 pointer-events-none">
                          {overlay}
                        </div>
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pagination && onPageChange && (
        <div className="flex items-center justify-start gap-4 border border-gray-200 border-t-0 bg-white p-4 rounded-b-md">
          <PageInfo pagination={pagination} />
          <PageNavigation pagination={pagination} onPageChange={onPageChange} />
        </div>
      )}
    </div>
  );
}
